import React from 'react';
import { Calendar as CalendarIcon } from 'lucide-react';
import { Card, CardContent } from '../ui';
import EntityLogo from '../ui/EntityLogo';
import { parseLocalDate } from '../../lib/dateUtils';
import { cn } from '../../lib/utils';
import { etiquetaDivision } from '../../lib/division';
import { rejillaPorSede, SIN_SEDE } from '../../lib/rejillaSedes';
import { enlaceSeguro } from '../../lib/enlaceSeguro';

// Rejilla de la jornada PROGRAMADA: filas = horarios, columnas = una por cancha de cada sede (la sede arriba), cada celda = el partido.
// `sedes` ([{ nombre, maps_url }], en el orden del torneo) solo pone orden y mapa; sin ella agrupa por el texto de `sede` de los partidos.
const CATEGORY_PALETTE = [
  'bg-amber-500/10 border-amber-500/30',
  'bg-emerald-500/10 border-emerald-500/30',
  'bg-sky-500/10 border-sky-500/30',
  'bg-violet-500/10 border-violet-500/30',
  'bg-rose-500/10 border-rose-500/30',
  'bg-teal-500/10 border-teal-500/30',
  'bg-orange-500/10 border-orange-500/30',
  'bg-fuchsia-500/10 border-fuchsia-500/30',
];

const buildCategoryColors = (nombres) => {
  const uniq = Array.from(new Set(nombres.filter(Boolean))).sort();
  const map = {};
  uniq.forEach((nombre, i) => { map[nombre] = CATEGORY_PALETTE[i % CATEGORY_PALETTE.length]; });
  return map;
};

// La division del juego (categoria + genero): dos divisiones de U14 son dos colores.
const etiquetaJuego = (j) => (j.categoria_nombre ? etiquetaDivision({ nombre: j.categoria_nombre, rama: j.rama }) : '');

const fmtFecha = (iso) => {
  const d = parseLocalDate(iso);
  return d ? d.toLocaleDateString('es-MX', { day: '2-digit', month: 'short', year: 'numeric' }) : null;
};

export default function RolJornadaGrid({ juegos = [], highlightName = null, onSelectJuego, sedes = [] }) {
  if (juegos.length === 0) {
    return (
      <Card>
        <CardContent className="py-12 text-center text-muted-foreground">
          <CalendarIcon className="mx-auto mb-3 h-10 w-10 opacity-30" />
          <p className="text-sm">Esta jornada todavía no tiene partidos.</p>
        </CardContent>
      </Card>
    );
  }

  const r = rejillaPorSede(juegos, { ordenSedes: (sedes || []).map((x) => x.nombre) });
  const columnas = r.columnas;
  const grupos = r.sedes.map((x) => ({
    nombre: x.nombre,
    n: x.canchas.length,
    maps: enlaceSeguro(((sedes || []).find((q) => q.nombre === x.nombre) || {}).maps_url),
  }));
  // Sin ninguna sede conocida no hay nada que titular: sin la fila "Sin sede".
  const soloSinSede = grupos.length === 1 && grupos[0].nombre === SIN_SEDE;
  const filasHora = r.filas.map((f) => ({ hora: f.hora || 'Sin horario', celdas: f.celdas }));

  const categoryColors = buildCategoryColors(juegos.map(etiquetaJuego));
  const fecha = fmtFecha(juegos.find((j) => j.fecha)?.fecha);

  return (
    <div className="space-y-3">
      {fecha && (
        <div className="flex flex-wrap items-center gap-3 text-sm text-muted-foreground">
          {fecha && <span className="flex items-center gap-1"><CalendarIcon className="h-4 w-4" /> {fecha}</span>}
        </div>
      )}

      <div className="overflow-x-auto rounded-lg border">
        <table className="w-full border-collapse text-sm">
          <thead>
            {!soloSinSede && (
            <tr className="border-b bg-muted/30">
              <th rowSpan={2} className="sticky left-0 z-10 whitespace-nowrap bg-muted/30 p-2 text-left font-medium text-muted-foreground">Hora</th>
              {grupos.map((g) => (
                <th key={g.nombre} colSpan={g.n} className="whitespace-nowrap border-l p-2 text-left font-semibold">
                  {g.nombre}
                  {g.maps && (
                    <a href={g.maps} target="_blank" rel="noopener noreferrer" className="ml-2 text-xs font-normal text-primary underline">mapa</a>
                  )}
                </th>
              ))}
            </tr>
            )}
            <tr className="border-b bg-muted/30">
              {soloSinSede && <th className="sticky left-0 z-10 whitespace-nowrap bg-muted/30 p-2 text-left font-medium text-muted-foreground">Hora</th>}
              {columnas.map((c, i) => (
                <th key={i} className="min-w-[220px] whitespace-nowrap p-2 text-left font-medium text-muted-foreground">
                  {c.cancha}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filasHora.map(({ hora, celdas }) => (
              <tr key={hora} className="border-b last:border-0">
                <td className="sticky left-0 z-10 whitespace-nowrap bg-background p-2 align-top font-medium">{hora}</td>
                {columnas.map((c, ci) => {
                  const cellJuegos = celdas[ci] || [];
                  return (
                    <td key={ci} className="p-2 align-top">
                      <div className="flex flex-col gap-1.5">
                        {cellJuegos.map((j) => {
                          const mine = highlightName && (j.local_club === highlightName || j.visita_club === highlightName);
                          return (
                          <div
                            key={j.juego_id}
                            onClick={onSelectJuego ? () => onSelectJuego(j.juego_id) : undefined}
                            title={onSelectJuego ? 'Capturar marcador / editar' : undefined}
                            className={cn('space-y-1 rounded-md border p-2', categoryColors[etiquetaJuego(j)],
                              mine && 'ring-2 ring-primary',
                              onSelectJuego && 'cursor-pointer transition hover:ring-2 hover:ring-primary/40')}
                          >
                            <p className="text-[11px] font-medium text-muted-foreground">{etiquetaJuego(j)}{j.grupo ? ` · Grupo ${j.grupo}` : ''}</p>
                            <div className="flex items-center justify-between gap-2">
                              <div className="flex min-w-0 items-center gap-1.5">
                                <EntityLogo src={j.local_logo} name={j.local_club} className="h-6 w-6 shrink-0" />
                                <span className="truncate text-xs font-medium">{j.local_club}</span>
                              </div>
                              {j.estatus === 'finalizado' && (
                                <span className="shrink-0 text-xs font-semibold tabular-nums">{j.marcador_local ?? 0}</span>
                              )}
                            </div>
                            <div className="flex items-center justify-between gap-2">
                              <div className="flex min-w-0 items-center gap-1.5">
                                <EntityLogo src={j.visita_logo} name={j.visita_club} className="h-6 w-6 shrink-0" />
                                <span className="truncate text-xs font-medium">{j.visita_club}</span>
                              </div>
                              {j.estatus === 'finalizado' && (
                                <span className="shrink-0 text-xs font-semibold tabular-nums">{j.marcador_visita ?? 0}</span>
                              )}
                            </div>
                          </div>
                          );
                        })}
                      </div>
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {Object.keys(categoryColors).length > 1 && (
        <div className="flex flex-wrap items-center gap-3">
          {Object.entries(categoryColors).map(([nombre, cls]) => (
            <span key={nombre} className="flex items-center gap-1.5 text-xs text-muted-foreground">
              <span className={cn('h-3 w-3 rounded-sm border', cls)} />
              {nombre}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}
